import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';

// tqmtype 구분값 (서버의 TQM_TYPES 와 같다)
const TQM_TYPES = ['자격증명', '역량강화', '정기교육', '기타'];
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.hwp,.hwpx,.doc,.docx,.xls,.xlsx,.zip';
const IMAGE_EXT = ['jpg', 'jpeg', 'png'];

const today = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const emptyFilters = () => ({ teachid: '', tqmtype: '', subject: '', dateFrom: '', dateTo: '' });
const emptyForm = (teachid = '') => ({
  teachid,
  tqmdate: today(),
  tqmtype: TQM_TYPES[0],
  tqmcenter: '',
  tqmsubject: '',
  sportdoc: '',
  file: null,
});

const extOf = (name) => (name && name.includes('.') ? name.split('.').pop().toLowerCase() : '');

// 증빙자료 파일 아이콘: PDF(빨강) / 이미지(초록) / 그 밖의 문서(파랑)
function EvidenceIcon({ filename }) {
  const ext = extOf(filename);
  const isPdf = ext === 'pdf';
  const isImage = IMAGE_EXT.includes(ext);
  const color = isPdf ? '#dc2626' : isImage ? '#16a34a' : '#2563eb';
  const label = isPdf ? 'PDF' : isImage ? 'IMG' : ext.slice(0, 4).toUpperCase() || 'FILE';
  return (
    <svg viewBox="0 0 24 28" width="22" height="26" aria-hidden="true">
      <path d="M3 1.5h12l6 6V25a1.5 1.5 0 0 1-1.5 1.5h-16A1.5 1.5 0 0 1 2 25V3A1.5 1.5 0 0 1 3.5 1.5z" fill="#ffffff" stroke={color} strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M15 1.5v5a1.5 1.5 0 0 0 1.5 1.5H21" fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" />
      <rect x="2.8" y="14" width="18.4" height="8" rx="1.2" fill={color} />
      <text x="12" y="20.3" textAnchor="middle" fontSize={label.length > 3 ? '5.6' : '6.6'} fontWeight="700" fill="#ffffff" fontFamily="Arial, sans-serif">
        {label}
      </text>
    </svg>
  );
}

// 교사 자격관리: 교사별 자격증명/역량강화/정기교육 등의 이력과 증빙자료를 관리한다.
export default function TeacherQualifications() {
  const [teachers, setTeachers] = useState([]); // 재직중인 원내교사
  const [filters, setFilters] = useState(emptyFilters());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState(null); // null=닫힘, 'new'=등록, tqmid=수정
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/teachers', { params: { location: 'internal' } }).then((res) => setTeachers(res.data));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.teachid) params.teachid = filters.teachid;
      if (filters.tqmtype) params.tqmtype = filters.tqmtype;
      if (filters.subject.trim()) params.subject = filters.subject.trim();
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      const res = await api.get('/teacher-quals', { params });
      setRows(res.data);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // 조건이 바뀌면 바로 다시 조회한다 (교육/자격증명내용 글자를 입력하는 중에는 잠깐 기다렸다가)
  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const editingRow = typeof editing === 'number' ? rows.find((r) => r.tqmid === editing) : null;

  const openNew = () => {
    setForm(emptyForm(filters.teachid));
    setEditing('new');
    setMessage('');
  };

  const openEdit = (r) => {
    setForm({
      teachid: r.teachid,
      tqmdate: r.tqmdate || today(),
      tqmtype: r.tqmtype,
      tqmcenter: r.tqmcenter,
      tqmsubject: r.tqmsubject,
      sportdoc: r.sportdoc,
      file: null,
    });
    setEditing(r.tqmid);
    setMessage('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.teachid) {
      setMessage('교사명을 선택하세요.');
      return;
    }
    if (!form.tqmdate) {
      setMessage('교육/입사일자를 입력하세요.');
      return;
    }
    if (!form.tqmsubject.trim()) {
      setMessage('교육/자격증명내용을 입력하세요.');
      return;
    }
    const data = new FormData();
    data.append('teachid', form.teachid);
    data.append('tqmdate', form.tqmdate);
    data.append('tqmtype', form.tqmtype);
    data.append('tqmcenter', form.tqmcenter);
    data.append('tqmsubject', form.tqmsubject);
    data.append('sportdoc', form.sportdoc);
    if (form.file) data.append('file', form.file);
    setSaving(true);
    setMessage('');
    try {
      if (editing === 'new') await api.post('/teacher-quals', data);
      else await api.put(`/teacher-quals/${editing}`, data);
      setEditing(null);
      await load();
      setMessage('저장되었습니다.');
    } catch (err) {
      setMessage(err.response?.data?.detail || '저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`${r.teacher_name} 교사의 "${r.tqmsubject}" (${r.tqmdate}) 자료를 삭제하시겠습니까?\n증빙자료 파일도 함께 삭제됩니다.`)) return;
    try {
      await api.delete(`/teacher-quals/${r.tqmid}`);
      await load();
      setMessage('삭제되었습니다.');
    } catch (err) {
      setMessage(err.response?.data?.detail || '삭제하지 못했습니다.');
    }
  };

  // PDF/이미지는 새 탭에서 바로 보고, 그 밖의 파일은 내려받는다
  const openFile = async (r) => {
    const ext = extOf(r.filename);
    const inline = ext === 'pdf' || IMAGE_EXT.includes(ext);
    // 비동기 요청이 끝난 뒤 열면 팝업 차단될 수 있어, 바로 볼 수 있는 파일은 클릭 시점에 빈 탭을 먼저 연다
    const win = inline ? window.open('', '_blank') : null;
    try {
      const res = await api.get(`/teacher-quals/${r.tqmid}/file`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      if (win) {
        win.location.href = url;
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = r.filename || 'file';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (win) win.close();
      setMessage('증빙자료 파일을 열 수 없습니다. 서버에 파일이 없거나 삭제되었을 수 있습니다.');
    }
  };

  const hasFilter = Object.values(filters).some((v) => v);

  return (
    <div className="page tq-page">
      <div className="page-header">
        <h2>교사자격관리</h2>
        {editing === null && (
          <button type="button" className="btn-primary tq-add-btn" onClick={openNew}>
            교사자격 등록
          </button>
        )}
      </div>

      {editing !== null && (
        <form className="card tq-form" onSubmit={handleSave}>
          <h3>{editing === 'new' ? '교사자격 등록' : '교사자격 수정'}</h3>
          <div className="tq-form-grid">
            <div className="form-field">
              <label>교사명</label>
              <select value={form.teachid} onChange={(e) => setForm({ ...form, teachid: e.target.value })}>
                <option value=""></option>
                {teachers.map((t) => (
                  <option key={t.teachid} value={t.teachid}>{t.name}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>교육/입사일자</label>
              <input type="date" value={form.tqmdate} onChange={(e) => setForm({ ...form, tqmdate: e.target.value })} />
            </div>
            <div className="form-field">
              <label>교육/자격구분</label>
              <select value={form.tqmtype} onChange={(e) => setForm({ ...form, tqmtype: e.target.value })}>
                {TQM_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>교육/증명기관</label>
              <input
                type="text"
                maxLength={30}
                value={form.tqmcenter}
                onChange={(e) => setForm({ ...form, tqmcenter: e.target.value })}
                placeholder="예) 한국항공대학교"
              />
            </div>
          </div>
          <div className="form-field">
            <label>교육/자격증명내용</label>
            <input
              type="text"
              maxLength={200}
              value={form.tqmsubject}
              onChange={(e) => setForm({ ...form, tqmsubject: e.target.value })}
              placeholder="교육 과정명 또는 자격증명 내용"
            />
          </div>
          <div className="tq-form-grid tq-form-grid-2">
            <div className="form-field">
              <label>증빙내용</label>
              <input
                type="text"
                maxLength={100}
                value={form.sportdoc}
                onChange={(e) => setForm({ ...form, sportdoc: e.target.value })}
                placeholder="예) 수료증, 자격증 사본"
              />
            </div>
            <div className="form-field">
              <label>증빙자료 파일</label>
              <input
                type="file"
                accept={ACCEPT}
                onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
              />
              <span className="std-hint">
                {editingRow?.filename ? `현재: ${editingRow.filename} (바꿀 때만 선택) · ` : ''}
                pdf, jpg, png, hwp, hwpx, doc, docx, xls, xlsx, zip / 30MB 이하
              </span>
            </div>
          </div>
          <div className="std-form-actions">
            <button type="submit" className="btn-primary std-save-btn" disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setEditing(null)}>취소</button>
          </div>
        </form>
      )}

      <div className="card">
        <div className="tq-filter-row">
          <div className="form-field">
            <label>교사명</label>
            <select value={filters.teachid} onChange={(e) => setFilter('teachid', e.target.value)}>
              <option value="">전체</option>
              {teachers.map((t) => (
                <option key={t.teachid} value={t.teachid}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label>교육/자격구분</label>
            <select value={filters.tqmtype} onChange={(e) => setFilter('tqmtype', e.target.value)}>
              <option value="">전체</option>
              {TQM_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="form-field tq-filter-subject">
            <label>교육/자격증명내용</label>
            <input
              type="text"
              value={filters.subject}
              onChange={(e) => setFilter('subject', e.target.value)}
              placeholder="포함된 글자 검색"
            />
          </div>
          <div className="form-field tq-filter-date">
            <label>교육/입사일자</label>
            <div className="tq-date-range">
              <input type="date" value={filters.dateFrom} onChange={(e) => setFilter('dateFrom', e.target.value)} />
              <span>~</span>
              <input type="date" value={filters.dateTo} onChange={(e) => setFilter('dateTo', e.target.value)} />
            </div>
          </div>
          <div className="form-field tq-filter-reset">
            <label>&nbsp;</label>
            <button type="button" className="btn-ghost tq-reset-btn" onClick={() => setFilters(emptyFilters())} disabled={!hasFilter}>
              초기화
            </button>
          </div>
        </div>

        {message && <div className="form-message std-message">{message}</div>}

        <div className="tq-count">총 {rows.length}건</div>
        <div className="table-scroll">
          <table className="data-table tq-table">
            <thead>
              <tr>
                <th className="tq-col-no">번호</th>
                <th className="tq-col-name">교사명</th>
                <th className="tq-col-date">교육/입사일자</th>
                <th className="tq-col-type">교육/자격구분</th>
                <th className="tq-col-center">교육/증명기관</th>
                <th>교육/자격증명내용</th>
                <th className="tq-col-doc">증빙내용</th>
                <th className="tq-col-file">증빙자료</th>
                <th className="tq-col-input">입력일자</th>
                <th className="tq-col-admin">관리</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.tqmid}>
                  <td>{rows.length - i}</td>
                  <td>{r.teacher_name}</td>
                  <td>{r.tqmdate}</td>
                  <td><span className={`tq-type tq-type-${TQM_TYPES.indexOf(r.tqmtype)}`}>{r.tqmtype}</span></td>
                  <td className="tq-left">{r.tqmcenter}</td>
                  <td className="tq-left">{r.tqmsubject}</td>
                  <td className="tq-left">{r.sportdoc}</td>
                  <td>
                    {r.has_file ? (
                      <button type="button" className="std-icon-btn" onClick={() => openFile(r)} title={`${r.filename} 열기`} aria-label="증빙자료 열기">
                        <EvidenceIcon filename={r.filename} />
                      </button>
                    ) : (
                      <span className="std-no-file">-</span>
                    )}
                  </td>
                  <td>{r.createat}</td>
                  <td className="std-admin">
                    <button type="button" className="btn-ghost std-small-btn" onClick={() => openEdit(r)}>수정</button>
                    <button type="button" className="btn-ghost std-small-btn std-del-btn" onClick={() => handleDelete(r)}>삭제</button>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={10} className="empty-row">
                    {hasFilter ? '조건에 맞는 자료가 없습니다.' : '등록된 자료가 없습니다. 위의 [교사자격 등록] 버튼으로 추가하세요.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
